// Pure helpers behind the invoices dashboard. No React, no DOM — everything
// here is a plain function over the invoice records the API returns.

// DRF serialises DecimalFields as strings, so every arithmetic path coerces.
const num = (v) => Number(v || 0);

const statusOf = (inv) => (inv.status || "DRAFT").toUpperCase();

export const money = (v) =>
  `$${num(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Issued but not yet collected. DRAFT is excluded deliberately: it has not
// been billed, so it belongs in the total without inflating what is owed.
export const OUTSTANDING_STATUSES = ["FINAL", "SENT"];

export function computeStats(invoices) {
  return (invoices || []).reduce(
    (acc, inv) => {
      const amount = num(inv.total);
      const status = statusOf(inv);
      acc.totalBilled += amount;
      acc.count += 1;
      if (OUTSTANDING_STATUSES.includes(status)) acc.outstanding += amount;
      else if (status === "PAID") acc.collected += amount;
      else if (status === "DRAFT") acc.draftCount += 1;
      return acc;
    },
    { totalBilled: 0, outstanding: 0, collected: 0, count: 0, draftCount: 0 }
  );
}

export function monthlySeries(invoices, months = 6) {
  const buckets = new Map();
  for (const inv of invoices || []) {
    const month = (inv.date || "").slice(0, 7);
    if (month.length !== 7) continue;
    buckets.set(month, (buckets.get(month) || 0) + num(inv.total));
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .slice(-months)
    .map(([month, value]) => ({ month, value }));
}

const csvCell = (value) => {
  const s = value == null ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv(rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = (rows || []).map((row) =>
    columns.map((c) => csvCell(c.format(row))).join(",")
  );
  return [header, ...body].join("\n");
}

// The status-filter value meaning "Final or Sent". A single-select dropdown
// cannot express two statuses, so this pseudo-status stands for the pair --
// which is exactly what the Outstanding KPI tile counts.
export const OUTSTANDING_FILTER = "OUTSTANDING";

export function matchesStatusFilter(invoice, filterStatus) {
  if (!filterStatus) return true;
  const status = statusOf(invoice || {});
  if (filterStatus === OUTSTANDING_FILTER) {
    return OUTSTANDING_STATUSES.includes(status);
  }
  return status === String(filterStatus).toUpperCase();
}

// One row per invoice LINE, with the invoice's columns repeated on each.
// `linesByInvoice` maps invoice id -> [{description, amount}].
//
// An invoice with no lines still gets a row, with both line cells blank:
// dropping it would make this export and the invoice-level one disagree on
// how many invoices exist, and a silently missing invoice is worse than an
// obviously empty one.
//
// The amount is written raw rather than through money(), so the column sums
// in a spreadsheet. That is deliberately unlike the invoice-level export,
// whose money columns are for reading.
export function toCsvWithLines(rows, columns, linesByInvoice) {
  const header = [
    ...columns.map((c) => csvCell(c.label)),
    csvCell("Line Description"),
    csvCell("Line Amount"),
  ].join(",");

  const body = [];
  for (const row of rows || []) {
    const base = columns.map((c) => csvCell(c.format(row)));
    const lines = (linesByInvoice && linesByInvoice[row.id]) || [];
    if (lines.length === 0) {
      body.push([...base, "", ""].join(","));
      continue;
    }
    for (const line of lines) {
      body.push([...base, csvCell(line.description), csvCell(line.amount)].join(","));
    }
  }
  return [header, ...body].join("\n");
}
